import React from 'react';
import { ChevronLeft, ShieldCheck, Lock, Globe, Server, Eye, AlertTriangle, BadgeCheck, KeyRound, Database, Fingerprint } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';

function Badge({ icon: Icon, label, color }) {
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${color}`}>
      <Icon className="w-3.5 h-3.5 flex-shrink-0" />
      <span className="text-[11px] font-semibold">{label}</span>
    </div>
  );
}

export default function Security() {
  const navigate = useNavigate();
  const effective = 'February 24, 2026';

  return (
    <div className="min-h-screen flex items-start justify-center px-4 py-8 md:py-12 relative z-10">
      <SEO title="Security" description="SurfSwell.ai Security — learn about our encryption, authentication, and data protection practices." path="/security" />
      <div className="w-full max-w-md bg-bg-paper/95 backdrop-blur-xl rounded-3xl border border-white/10 shadow-2xl overflow-hidden min-h-[85vh] flex flex-col" data-testid="security-page">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/5">
          <button data-testid="security-back-btn" onClick={() => navigate('/profile')} className="p-1.5 hover:bg-white/10 rounded-full transition-colors">
            <ChevronLeft className="w-5 h-5 text-muted-foreground" />
          </button>
          <h1 className="font-heading text-base font-semibold">Security</h1>
        </div>

        <div className="px-5 py-5 space-y-6 text-xs text-muted-foreground leading-relaxed max-h-[calc(100vh-200px)] overflow-y-auto">
          <p className="text-[10px] text-muted-foreground/60">Last updated: {effective}</p>

          {/* Hero Statement */}
          <div className="bg-green-500/[0.06] border border-green-500/15 rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-white mb-1">Your data is protected</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Swell is built with security at its foundation. We implement industry-standard security controls and continuously monitor our infrastructure to protect your information.
                </p>
              </div>
            </div>
          </div>

          {/* Compliance Alignment */}
          <section>
            <h2 className="text-sm font-semibold text-white mb-3">Standards Alignment</h2>
            <p className="mb-3">
              Swell's security architecture is designed in alignment with the following recognized industry frameworks and standards:
            </p>
            <div className="flex flex-wrap gap-2 mb-3">
              <Badge icon={BadgeCheck} label="SOC 2 Type II" color="text-blue-400 bg-blue-500/10 border-blue-500/15" />
              <Badge icon={BadgeCheck} label="ISO 27001" color="text-violet-400 bg-violet-500/10 border-violet-500/15" />
              <Badge icon={BadgeCheck} label="NIST 800-171" color="text-amber-400 bg-amber-500/10 border-amber-500/15" />
            </div>
            <p className="text-[10px] text-muted-foreground/50 leading-relaxed">
              Our controls and practices align with the principles outlined in SOC 2 (Trust Services Criteria), ISO/IEC 27001:2022 (Information Security Management Systems), and NIST SP 800-171 Rev. 2 (Protecting Controlled Unclassified Information). We apply these frameworks as guiding standards for our security program.
            </p>
          </section>

          {/* Founder Credentials */}
          <section>
            <h2 className="text-sm font-semibold text-white mb-2">Leadership & Expertise</h2>
            <div className="bg-bg-subtle/60 border border-white/5 rounded-xl p-3">
              <p className="text-xs text-muted-foreground leading-relaxed">
                Swell is created by a founder who holds the <span className="text-white font-medium">CISSP (Certified Information Systems Security Professional)</span> and <span className="text-white font-medium">CompTIA SecurityX</span> credentials, demonstrating advanced expertise in information security architecture, risk management, and security engineering. These certifications are governed by (ISC)&sup2; and CompTIA respectively, and require ongoing continuing education to maintain.
              </p>
            </div>
          </section>

          {/* Encryption */}
          <section>
            <h2 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
              <Lock className="w-3.5 h-3.5 text-green-400" />
              Encryption
            </h2>

            <h3 className="text-xs font-semibold text-white/80 mt-3 mb-1">Data in Transit</h3>
            <p>
              All data transmitted between your device and our servers is encrypted using <span className="text-white font-medium">TLS 1.3</span> with the <span className="text-white font-medium">TLS_AES_256_GCM_SHA384</span> cipher suite. This provides authenticated encryption using AES-256 in Galois/Counter Mode, the strongest widely-deployed transport encryption available. TLS certificates are issued by Google Trust Services and managed via Cloudflare's edge network, ensuring certificate validity and automatic renewal.
            </p>

            <h3 className="text-xs font-semibold text-white/80 mt-3 mb-1">Data at Rest</h3>
            <p>
              Stored data is protected using <span className="text-white font-medium">AES-256 encryption at rest</span>. Database storage volumes utilize full-disk encryption, and sensitive credentials (API keys, session tokens) are stored using encrypted fields with access restricted to authorized application processes only.
            </p>

            <h3 className="text-xs font-semibold text-white/80 mt-3 mb-1">Key Management</h3>
            <p>
              Encryption keys are managed through infrastructure-level key management services with automatic rotation. Application-level secrets are stored in environment-isolated configurations, never hardcoded or committed to source control.
            </p>
          </section>

          {/* Authentication */}
          <section>
            <h2 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
              <Fingerprint className="w-3.5 h-3.5 text-violet-400" />
              Authentication & Access Control
            </h2>
            <ul className="list-disc ml-4 space-y-1.5">
              <li><span className="text-white font-medium">OAuth 2.0 / OpenID Connect:</span> Authentication is handled via Google SSO using industry-standard OAuth 2.0 and OpenID Connect protocols. We never store or handle your Google password.</li>
              <li><span className="text-white font-medium">Session management:</span> Authenticated sessions use cryptographically secure, randomly generated tokens with defined expiration periods. Sessions are validated on every request and expired sessions are automatically invalidated.</li>
              <li><span className="text-white font-medium">Role-based access control (RBAC):</span> Administrative functions are restricted to explicitly authorized accounts with role-based permissions enforced at the API level.</li>
              <li><span className="text-white font-medium">Account protection:</span> Accounts that violate our terms are subject to immediate suspension, enforced at the authentication layer to prevent further access.</li>
            </ul>
          </section>

          {/* Infrastructure */}
          <section>
            <h2 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
              <Server className="w-3.5 h-3.5 text-blue-400" />
              Infrastructure Security
            </h2>
            <ul className="list-disc ml-4 space-y-1.5">
              <li><span className="text-white font-medium">Cloud-native architecture:</span> Our application runs on containerized infrastructure within a Kubernetes-orchestrated environment, providing isolation, automatic scaling, and rapid incident response capabilities.</li>
              <li><span className="text-white font-medium">DDoS protection:</span> Edge-level DDoS mitigation is provided by Cloudflare, protecting against volumetric, protocol, and application-layer attacks.</li>
              <li><span className="text-white font-medium">Rate limiting:</span> API endpoints implement rate limiting to prevent abuse, brute force attempts, and resource exhaustion.</li>
              <li><span className="text-white font-medium">Network segmentation:</span> Backend services, databases, and external API integrations are isolated within separate network segments with least-privilege access policies.</li>
            </ul>
          </section>

          {/* API & Third-Party */}
          <section>
            <h2 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
              <Globe className="w-3.5 h-3.5 text-primary" />
              API & Third-Party Security
            </h2>
            <ul className="list-disc ml-4 space-y-1.5">
              <li><span className="text-white font-medium">Secure API communication:</span> All third-party API calls (weather data, marine forecasts, AI services) are made over HTTPS/TLS. API keys are stored securely and never exposed to client-side code.</li>
              <li><span className="text-white font-medium">Input validation:</span> All user inputs are validated and sanitized at the API boundary using Pydantic schema validation to prevent injection attacks.</li>
              <li><span className="text-white font-medium">AI data handling:</span> Conversations with Swell (our AI surf coach) are processed through Google's Gemini API, which does not use your data for model training. Chat history is stored per-user and can be deleted at any time.</li>
              <li><span className="text-white font-medium">Vendor assessment:</span> Third-party services are evaluated for security posture before integration. We only integrate with providers that maintain their own security certifications and compliance programs.</li>
            </ul>
          </section>

          {/* Data Protection */}
          <section>
            <h2 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
              <Database className="w-3.5 h-3.5 text-secondary" />
              Data Protection & Privacy
            </h2>
            <ul className="list-disc ml-4 space-y-1.5">
              <li><span className="text-white font-medium">Data minimization:</span> We collect only the data necessary to provide and improve our services, in alignment with the principle of least privilege.</li>
              <li><span className="text-white font-medium">Data residency:</span> Application data is stored within secured cloud infrastructure with access controls restricting data to authorized processes and personnel.</li>
              <li><span className="text-white font-medium">Right to deletion:</span> Users may request complete deletion of their account and associated data at any time. Deletion requests are processed within 30 days, consistent with CCPA and GDPR requirements.</li>
              <li><span className="text-white font-medium">Breach notification:</span> In the event of a data breach affecting your personal information, we will notify affected users within 72 hours of confirmed discovery, consistent with GDPR Article 33 requirements.</li>
            </ul>
          </section>

          {/* Monitoring */}
          <section>
            <h2 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
              <Eye className="w-3.5 h-3.5 text-amber-400" />
              Monitoring & Incident Response
            </h2>
            <ul className="list-disc ml-4 space-y-1.5">
              <li><span className="text-white font-medium">Continuous monitoring:</span> Application health, API availability, and security events are monitored continuously. Anomalous behavior triggers automated alerts for immediate investigation.</li>
              <li><span className="text-white font-medium">Incident response:</span> We maintain a documented incident response plan with defined severity levels, escalation procedures, and post-incident review processes.</li>
              <li><span className="text-white font-medium">Logging & audit trail:</span> Security-relevant events (authentication, authorization, administrative actions) are logged with tamper-resistant audit trails for forensic analysis.</li>
            </ul>
          </section>

          {/* Vulnerability */}
          <section>
            <h2 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
              Vulnerability Management
            </h2>
            <ul className="list-disc ml-4 space-y-1.5">
              <li><span className="text-white font-medium">Dependency management:</span> Application dependencies are regularly reviewed and updated to address known vulnerabilities (CVEs).</li>
              <li><span className="text-white font-medium">Secure development:</span> Our development practices include code review, input validation, output encoding, and adherence to OWASP Top 10 mitigation strategies.</li>
              <li><span className="text-white font-medium">Responsible disclosure:</span> We welcome responsible security research. If you discover a vulnerability, please chat with <span className="text-primary font-medium">Swell</span> to open a support ticket and we will investigate promptly.</li>
            </ul>
          </section>

          {/* Your Controls */}
          <section>
            <h2 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
              <KeyRound className="w-3.5 h-3.5 text-green-400" />
              Your Security Controls
            </h2>
            <p className="mb-2">You have direct control over the following security-relevant actions:</p>
            <ul className="list-disc ml-4 space-y-1.5">
              <li>Sign out from any device at any time via your Profile</li>
              <li>Delete your AI chat history at any time</li>
              <li>Request complete account and data deletion</li>
              <li>Review your active sessions and connected integrations</li>
              <li>Control what personal information is shared in your profile</li>
            </ul>
          </section>

          {/* Legal */}
          <section className="border-t border-white/5 pt-4">
            <p className="text-[10px] text-muted-foreground/40 leading-relaxed">
              <span className="text-white/60 font-medium">Offworld Ventures LLC</span> | 2219 Main Street, Santa Monica, California 90405<br /><br />
              This security overview is provided for informational purposes and describes the security measures we implement to protect user data. References to SOC 2, ISO 27001, and NIST 800-171 indicate our alignment with these frameworks' principles and controls; they do not constitute a claim of formal certification unless explicitly stated. Our security program is subject to continuous improvement and periodic review.<br /><br />
              For security inquiries, responsible disclosure, or to report a concern, chat with <span className="text-white/60">Swell</span> to open a support ticket.
            </p>
          </section>

          {/* Changelog */}
          <section className="border-t border-white/5 pt-4">
            <h2 className="text-sm font-semibold text-white mb-3">Security Changelog</h2>
            <div className="space-y-3">
              <div className="bg-bg-subtle/40 rounded-xl p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] text-white/70 font-semibold">v1.0 — February 24, 2026</span>
                  <span className="text-[8px] bg-primary/15 text-primary px-1.5 py-0.5 rounded">Current</span>
                </div>
                <p className="text-[10px] text-muted-foreground/60">Initial security overview. Published standards alignment (SOC 2, ISO 27001, NIST 800-171), encryption details (TLS 1.3, AES-256), authentication and access control, infrastructure security, API and third-party security, data protection, monitoring and incident response, vulnerability management, and user security controls.</p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
